// Mock data for 10 X Living Costa Rica

export const siteConfig = {
  name: "10 X Living",
  tagline: "Sustainable Living in Costa Rica",
  description: "A thoughtfully designed residential community where dome homes, renewable energy, and intentional living create harmony with nature."
};

export const heroContent = {
  title: "10 X Living",
  subtitle: "Costa Rica",
  headline: "Where Sustainable Living Meets Natural Beauty",
  description: "A thoughtfully designed residential community in Costa Rica, where dome homes, renewable energy, and intentional living create harmony with nature.",
  image: "https://customer-assets.emergentagent.com/job_6e992db2-663b-4e0c-b681-6f4fb21a5a30/artifacts/b5tavtdt_Zoomed%20out%20photo%20of%20the%205%20domes%20with%20main%20office%20house%20in%20the%20middle.jpeg"
};

export const visionContent = {
  title: "Our Vision",
  headline: "Designing for Generations",
  intro: "10 X Living is more than a development—it's a commitment to sustainable well-being that honors both people and place.",
  paragraphs: [
    "We believe the way we live should reflect the values we hold. Every decision at 10 X Living—from the orientation of each dome to the depth of our underground utilities—is made with intention.",
    "Our vision extends beyond individual homes to encompass a complete ecosystem of shared resources, community spaces, and regenerative practices that will thrive for generations.",
    "Set in the stunning landscape of Costa Rica, we're creating a sanctuary where modern comfort meets ancient wisdom, where technology serves nature, and where community emerges organically from shared purpose."
  ],
  principles: [
    {
      title: "Regenerative Design",
      description: "Every element contributes to the health of the whole system"
    },
    {
      title: "Community First",
      description: "Shared spaces and resources strengthen connections"
    },
    {
      title: "Long-term Thinking",
      description: "Building for our children and their children"
    },
    {
      title: "Natural Integration",
      description: "Working with the land, not against it"
    }
  ],
  image: "https://customer-assets.emergentagent.com/job_6e992db2-663b-4e0c-b681-6f4fb21a5a30/artifacts/ftlveior_lake.jpg"
};

export const communityContent = {
  title: "Community & Lifestyle",
  headline: "Intentional Living, Naturally",
  intro: "Life at 10 X Living centers on connection—with neighbors, with nature, and with a shared vision of sustainable living.",
  mainImage: "https://customer-assets.emergentagent.com/job_6e992db2-663b-4e0c-b681-6f4fb21a5a30/artifacts/b5tavtdt_Zoomed%20out%20photo%20of%20the%205%20domes%20with%20main%20office%20house%20in%20the%20middle.jpeg",
  domeArchitecture: {
    title: "Dome Architecture",
    description: "Geodesic dome homes offer exceptional structural integrity, natural temperature regulation, and harmonious integration with the landscape.",
    image: "https://customer-assets.emergentagent.com/job_6e992db2-663b-4e0c-b681-6f4fb21a5a30/artifacts/xohhfb69_Photo%20of%20an%20individual%20dome%20home.jpeg"
  },
  zones: [
    {
      name: "Residential Zone",
      description: "Dome homes positioned for privacy, views, and optimal solar orientation.",
      features: ["Individual dome plots", "Private outdoor spaces", "Underground utility connections"]
    },
    {
      name: "Central Commons",
      description: "The heart of the community, housing shared facilities and the main solar installation.",
      features: ["Main building with solar array", "Community kitchen", "Gathering spaces", "Battery storage"]
    },
    {
      name: "Agricultural Zone",
      description: "Productive gardens and orchards cultivated using organic and biodynamic methods.",
      features: ["Vegetable gardens", "Fruit orchards", "Composting facilities", "Tool storage"]
    },
    {
      name: "Natural Areas",
      description: "Preserved wilderness, walking trails, and the natural hot springs.",
      features: ["Hot springs", "Walking trails", "Wildlife corridors", "Native plantings"]
    }
  ],
  lifestyle: [
    "Remote work with reliable connectivity",
    "Weekly community meals and gatherings",
    "Wellness programs and nature activities",
    "Educational workshops on sustainability",
    "Quiet hours and respect for solitude",
    "Family-friendly environment"
  ]
};

export const cultureContent = {
  title: "10 X Culture",
  headline: "Dimensions of Well-Being",
  intro: "The following is an outline of one version of an ideal micro-culture. This Costa Rican community tips its hat toward this ideal and leans in this direction wherever feasible.",
  hexagonImage: "https://customer-assets.emergentagent.com/job_6e992db2-663b-4e0c-b681-6f4fb21a5a30/artifacts/xucdzec0_Shape4%20copy.png",
  dimensions: [
    // Row 1
    { name: "Roles", url: "https://www.10xliving.org/roles" },
    { name: "Waste", url: "https://www.10xliving.org/waste" },
    { name: "Animals", url: "https://www.10xliving.org/animals" },
    { name: "Money", url: "https://www.10xliving.org/money-and-mediums-of-exchange" },
    { name: "Education", url: "https://www.10xliving.org/education" },
    { name: "Justice", url: "https://www.10xliving.org/justice" },
    { name: "Nature", url: "https://www.10xliving.org/nature" },
    // Row 2
    { name: "Governance", url: "https://www.10xliving.org/governance" },
    { name: "Spirituality", url: "https://www.10xliving.org/spirituality" },
    { name: "Connection", url: "https://www.10xliving.org/connection" },
    { name: "Parenting", url: "https://www.10xliving.org/parenting" },
    { name: "Relationships", url: "https://www.10xliving.org/relationships" },
    { name: "Creativity", url: "https://www.10xliving.org/creativity" },
    { name: "Work", url: "https://www.10xliving.org/work" },
    { name: "Play", url: "https://www.10xliving.org/play" },
    // Row 3
    { name: "Health", url: "https://www.10xliving.org/health" },
    { name: "Movement", url: "https://www.10xliving.org/movement" },
    { name: "Dance", url: "https://www.10xliving.org/dance" },
    { name: "Gender", url: "https://www.10xliving.org/gender-balanced" },
    { name: "Gardens", url: "https://www.10xliving.org/butterfly-gardens" },
    { name: "Self-Defense", url: "https://www.10xliving.org/self-defense" },
    { name: "Diet", url: "https://www.10xliving.org/diet" },
    // Row 4
    { name: "Yoga", url: "https://www.10xliving.org/yoga" },
    { name: "Music", url: "https://www.10xliving.org/music" },
    { name: "Art", url: "https://www.10xliving.org/art" },
    { name: "Stories", url: "https://www.10xliving.org/stories" },
    { name: "Aging", url: "https://www.10xliving.org/aging" },
    { name: "Death", url: "https://www.10xliving.org/death" },
    { name: "Space", url: "https://www.10xliving.org/space" },
    { name: "Time", url: "https://www.10xliving.org/time" }
  ]
};

export const pricingContent = {
  title: "Pricing",
  headline: "Investment & Community Fees",
  intro: "10 X Living is a purpose-driven wellbeing community in Costa Rica, designed for individuals and couples who want to live sustainably while contributing to a larger vision.",
  subIntro: "The community consists of five private geodesic domes, available for single or double occupancy. Each dome qualifies toward a Costa Rican investment residency visa.",
  investmentOptions: {
    title: "Investment Options (One-Time)",
    options: [
      { name: "Single Occupancy Dome", price: "$150,000 USD" },
      { name: "Double Occupancy Dome", price: "$175,000 USD" }
    ],
    note: "This one-time investment secures your private dome and your place in the 10 X Living community."
  },
  monthlyFees: {
    title: "Monthly Land, Maintenance & Community Fees",
    sections: [
      {
        name: "Land & Maintenance Fee",
        prices: [
          "$550 per person / month",
          "$770 per couple / month"
        ],
        description: "This fee covers the ongoing care, maintenance, and shared infrastructure of the community.",
        included: [
          "One shared Starlink internet terminal (Additional terminals may be added by residents at their own expense)",
          "One shared electric vehicle with free on-site charging",
          "Security guard for the property",
          "Roof and rain maintenance, including any leaks in domes or community buildings",
          "Air conditioning maintenance",
          "Appliance maintenance for shared washer, dryer, stove, and refrigerator",
          "Plumbing and septic system maintenance",
          "Heated pool equipment maintenance"
        ]
      },
      {
        name: "Security Contribution",
        prices: ["$100 per person / month"],
        description: "This fee supports dedicated on-site property security."
      }
    ]
  },
  included: {
    title: "What's Included With Residency",
    categories: [
      {
        name: "Private Infrastructure",
        items: [
          "Your own personal geodesic dome",
          "Solar-powered electricity, designed for low or no maintenance for the first 10 years",
          "Clean, fresh well water sourced directly from the land",
          "Perimeter fencing for privacy and safety"
        ]
      },
      {
        name: "Shared Community Amenities",
        items: [
          "Solar-heated hot spring pool using fresh well water",
          "Shared community kitchen",
          "Shared community living space",
          "Shared media room",
          "Shared office / coworking space",
          "Shared laundry facilities",
          "Designated garden areas for growing fruits and vegetables"
        ]
      },
      {
        name: "Guests & Occupancy",
        items: [
          "Domes may be single or double occupancy",
          "Residents may host one guest at a time, sharing their dome"
        ]
      }
    ]
  },
  carbonNegative: {
    title: "Carbon-Negative Living",
    description: "Each resident receives a Carbon Negative Certificate through the planting of 1,500 trees per resident.",
    note: "As a community, all residents of 10 X Living are carbon negative by design."
  },
  purposeDriven: {
    title: "A Purpose-Driven Community",
    intro: "10 X Living is intentionally designed for people who want more than just a place to live.",
    description: "All residents are selected based on their interest in:",
    interests: [
      "Contributing skills, time, and passion",
      "Supporting sustainability, wellbeing, and meaningful impact"
    ],
    commitment: "Each community member commits to a minimum of five hours per week of volunteer contribution, aligned with their skills and interests."
  }
};

export const sensesContent = {
  title: "10 X Senses",
  headline: "Honoring All Five Senses",
  image: "https://customer-assets.emergentagent.com/job_6e992db2-663b-4e0c-b681-6f4fb21a5a30/artifacts/pdhizd5r_10%20X%20Senses.png",
  senses: [
    {
      name: "Sound",
      description: "All machinery is electric to eliminate gas-powered equipment. The domes provide sound insulation to minimize noise pollution. The site has been selected for its quiet location."
    },
    {
      name: "Touch",
      description: "Community animal interaction is supported and encouraged."
    },
    {
      name: "Smell",
      description: "Synthetic fragrances are avoided. Natural scents and essential oils only."
    },
    {
      name: "Taste",
      description: "On-site organic food."
    },
    {
      name: "Sight",
      description: "Beautiful domes and gardens."
    }
  ]
};

export const aboutContent = {
  title: "About",
  headline: "The Story Behind 10 X Living",
  intro: "10 X Living emerged from a simple question: How might we live in greater harmony with nature while building meaningful community?",
  story: [
    "This project began not with architects or investors, but with a vision of sustainable well-being that honors both people and planet.",
    "Located in one of the most biodiverse regions on Earth, Costa Rica offered the perfect setting—a country committed to environmental protection, with a culture that values 'pura vida' and community connection.",
    "Every aspect of 10 X Living has been developed with intention: dome homes chosen for their efficiency and resilience, centralized solar to maximize clean energy production, underground utilities to preserve the landscape, and shared gardens to foster both food security and community bonds."
  ],
  values: [
    {
      title: "Sustainability",
      description: "Environmental stewardship guides every decision we make."
    },
    {
      title: "Community",
      description: "Meaningful connections arise from shared spaces and purposes."
    },
    {
      title: "Intention",
      description: "Thoughtful design creates spaces that support well-being."
    },
    {
      title: "Legacy",
      description: "We build for generations, not just for today."
    }
  ],
  image: "https://images.pexels.com/photos/16714671/pexels-photo-16714671.jpeg"
};

export const contactContent = {
  title: "Contact",
  headline: "Begin the Conversation",
  intro: "If our vision resonates with you, we'd welcome the opportunity to connect.",
  note: "10 X Living is designed for those seeking more than a home—it's for those ready to participate in building a sustainable community. We look forward to learning about your aspirations.",
  fields: [
    { name: "name", label: "Name", type: "text", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "phone", label: "Phone", type: "tel", required: false },
    { name: "location", label: "Current Location", type: "text", required: false },
    { name: "message", label: "Tell us about yourself and what draws you to 10 X Living", type: "textarea", required: true }
  ]
};

export const navigationItems = [
  { name: "Home", path: "/" },
  { name: "Vision", path: "/vision" },
  { name: "Community", path: "/community" },
  { name: "Pricing", path: "/pricing" },
  { name: "10 X Culture", path: "https://10xliving.org/culture", external: true },
  { name: "10 X Senses", path: "/senses" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" }
];

export const footerContent = {
  tagline: "Sustainable living in harmony with Costa Rica's natural beauty.",
  copyright: `© ${new Date().getFullYear()} 10 X Living. All rights reserved.`,
  links: [
    { name: "Privacy Policy", path: "/privacy" },
    { name: "Terms", path: "/terms" }
  ]
};
